import React from 'react'
import {
  MailIcon,
  MessageSquareIcon,
  PhoneIcon,
  PhoneIncomingIcon,
  PhoneOutgoingIcon,
  SmartphoneIcon,
} from 'lucide-react'
import type { ConversationAction } from '../../api/conversationActions'
import type { ConversationLabel } from '../../api/conversationLabels'
import type { InboxAssignee, InboxFeedItem } from '../../types/inbox'
import { isEntityUnlinked } from '../../lib/inboxPermissions'
import { handlingStatusBadgeClass, handlingStatusLabel } from '../../lib/inboxFilters'
import { InboxListItemActionsMenu } from './InboxListItemActionsMenu'

function formatTime(dateStr: string | null) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)
  if (diffMin < 1) return 'now'
  if (diffMin < 60) return `${diffMin}m`
  if (diffHr < 24) return `${diffHr}h`
  if (diffDay < 7) return d.toLocaleDateString(undefined, { weekday: 'short' })
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function channelMeta(channel: InboxFeedItem['channel']) {
  switch (channel) {
    case 'whatsapp':
      return { color: 'text-green-600', Icon: MessageSquareIcon }
    case 'sms':
      return { color: 'text-blue-600', Icon: SmartphoneIcon }
    case 'email':
      return { color: 'text-orange-600', Icon: MailIcon }
    default:
      return { color: 'text-violet-600', Icon: PhoneIcon }
  }
}

interface UnifiedInboxListItemProps {
  item: InboxFeedItem
  active: boolean
  labels: ConversationLabel[]
  labelIds: string[]
  assignees: InboxAssignee[]
  canAssignOthers: boolean
  currentUserId: string | null
  onSelect: (item: InboxFeedItem) => void
  onLink: (item: InboxFeedItem) => void
  onAssign: (item: InboxFeedItem, userId: string | null) => void
  onLabel: (item: InboxFeedItem) => void
  onResolve: (item: InboxFeedItem) => void
  onLifecycleAction: (item: InboxFeedItem, action: ConversationAction) => void
  bulkSelected?: boolean
  onBulkToggle?: (item: InboxFeedItem) => void
}

function slaChip(item: InboxFeedItem) {
  if (!item.slaDueAt) return null
  const due = new Date(item.slaDueAt)
  const overdue = due.getTime() < Date.now()
  if (!overdue && item.handlingStatus === 'resolved') return null
  return {
    key: 'sla',
    label: overdue ? 'SLA overdue' : 'SLA',
    className: overdue ? 'text-red-700 bg-red-50' : 'text-gray-500 bg-gray-100',
  }
}

export function UnifiedInboxListItem({
  item,
  active,
  labels,
  labelIds,
  assignees,
  canAssignOthers,
  currentUserId,
  onSelect,
  onLink,
  onAssign,
  onLabel,
  onResolve,
  onLifecycleAction,
  bulkSelected = false,
  onBulkToggle,
}: UnifiedInboxListItemProps) {
  const meta = channelMeta(item.channel)
  const Icon = meta.Icon
  const rowLabels = labels.filter((l) => labelIds.includes(l.id))

  const secondaryChips: Array<{ key: string; label: string; className: string }> = []
  if (isEntityUnlinked(item)) {
    secondaryChips.push({ key: 'unlinked', label: 'Unlinked', className: 'text-amber-700 bg-amber-50' })
  }
  if (!item.assignedToUserId && item.handlingStatus === 'open') {
    secondaryChips.push({ key: 'unassigned', label: 'Unassigned', className: 'text-gray-500 bg-gray-100' })
  }
  if (item.handlingStatus !== 'open') {
    secondaryChips.push({
      key: 'status',
      label: handlingStatusLabel(item.handlingStatus),
      className: handlingStatusBadgeClass(item.handlingStatus),
    })
  }
  const sla = slaChip(item)
  if (sla) secondaryChips.push(sla)
  for (const label of rowLabels) {
    secondaryChips.push({ key: label.id, label: label.name, className: 'text-gray-600 bg-gray-100' })
  }
  const visibleChips = secondaryChips.slice(0, 2)
  const hiddenChipCount = secondaryChips.length - visibleChips.length

  const preview =
    item.channel === 'email' && item.subject ? item.subject : item.preview

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(item)}
      className={`group relative w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer transition-colors ${
        active ? 'bg-blue-50 border-l-2 border-l-blue-600' : 'hover:bg-gray-50 border-l-2 border-l-transparent'
      }`}
    >
      {onBulkToggle && (
        <input
          type="checkbox"
          checked={bulkSelected}
          onChange={(e) => {
            e.stopPropagation()
            onBulkToggle(item)
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          aria-label="Select for bulk action"
        />
      )}

      <div className="flex-shrink-0 w-4 flex items-center justify-center">
        {item.channel === 'call' && item.direction === 'outbound' ? (
          <PhoneOutgoingIcon className={`w-3.5 h-3.5 ${meta.color}`} />
        ) : item.channel === 'call' ? (
          <PhoneIncomingIcon className={`w-3.5 h-3.5 ${meta.color}`} />
        ) : (
          <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`flex-1 text-sm truncate ${item.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}
          >
            {item.contactName}
          </span>
          <span className="flex items-center flex-shrink-0 gap-1">
            {item.unreadCount > 0 && (
              <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" title="Unread" />
            )}
            <span className="text-[11px] text-gray-400 tabular-nums">{formatTime(item.sortAt)}</span>
            <InboxListItemActionsMenu
              item={item}
              assignees={assignees}
              canAssignOthers={canAssignOthers}
              currentUserId={currentUserId}
              onLink={() => onLink(item)}
              onAssign={(userId) => onAssign(item, userId)}
              onLabel={() => onLabel(item)}
              onResolve={() => onResolve(item)}
              onLifecycleAction={(action) => onLifecycleAction(item, action)}
            />
          </span>
        </div>
        <p className={`text-xs truncate ${item.unreadCount > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
          {preview}
        </p>
        {secondaryChips.length > 0 && (
          <div className="hidden group-hover:flex items-center gap-1 mt-0.5 flex-wrap">
            {visibleChips.map((chip) => (
              <span
                key={chip.key}
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${chip.className}`}
              >
                {chip.label}
              </span>
            ))}
            {hiddenChipCount > 0 && (
              <span className="text-[10px] text-gray-400">+{hiddenChipCount}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
